import {
  STOCK_COUNT_KIND_LABELS,
  STOCK_COUNT_KINDS,
  STOCK_COUNT_STATUS_LABELS,
  STOCK_COUNT_STATUSES,
  type StockCount,
  stockCountKindSchema,
  stockCountStatusSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  MoneyText,
  Select,
  SkeletonRows,
  StatusBadge,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ClipboardPen, Plus } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CountDialog } from "../../../../features/market/count-dialog.js";
import { moneyExport } from "../../../../features/market/market-values.js";
import {
  type CountFilters,
  countsQuery,
  useCounts,
  useMarketPermissions,
} from "../../../../features/market/queries.js";
import {
  CountStatusBadge,
  LedgerStatus,
  MarketPage,
  marketSearchShape,
  MarketStationField,
  NoStations,
  useMarketStations,
} from "../../../../features/market/shared.js";
import { compactSearch } from "../../../../features/shifts/shift-values.js";
import { quickActionSearch, useOpenFromQuickAction } from "../../../../lib/hooks.js";
import { api } from "../../../../lib/api.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../../lib/excel.js";
import { formatDateTime } from "../../../../lib/format.js";

const searchSchema = z.object({
  ...marketSearchShape,
  status: stockCountStatusSchema.optional().catch(undefined),
  kind: stockCountKindSchema.optional().catch(undefined),
  ...quickActionSearch,
});

export const Route = createFileRoute("/_app/market/sayimlar/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: CountsRoute,
});

/**
 * Stok sayımları (Prompt 16 — web). Sayım açılır (tam / kısmi / döngüsel), tablet ekranında barkodla okutulur,
 * gönderilir, fark raporu incelenip onaylanır (`market.count.approve`, step-up). Açma / okutma / gönderme
 * `market.count.manage`.
 */
function CountsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <MarketPage
      title={t("menu.marketCounts")}
      description={t("market.counts.description")}
      breadcrumb={[{ label: t("menu.marketCounts") }]}
    >
      {({ companyId }) => <CountsBody companyId={companyId} />}
    </MarketPage>
  );
}

function CountsBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const permissions = useMarketPermissions();
  const station = useMarketStations(companyId);
  const [creating, setCreating] = useState(false);
  useOpenFromQuickAction(
    search.yeni === "1",
    () => setCreating(true),
    permissions.canManageCounts && !station.isLoading && station.stations.length > 0,
  );

  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  const filters: CountFilters = {
    stationId: station.stationId ?? undefined,
    status: search.status,
    kind: search.kind,
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useCounts(companyId, filters, pages.cursor);

  const columns = useMemo<DataTableColumn<StockCount>[]>(
    () => [
      {
        id: "countNo",
        header: t("market.counts.fields.countNo"),
        hideable: false,
        exportValue: (row) => row.countNo,
        cell: (row) => (
          <Link
            to="/market/sayimlar/$id"
            params={{ id: row.id }}
            className="grid gap-0.5 underline underline-offset-4"
          >
            <span className="font-mono whitespace-nowrap">{row.countNo}</span>
            <span className="text-xs text-muted-foreground">{formatDateTime(row.snapshotAt)}</span>
          </Link>
        ),
      },
      {
        id: "station",
        header: t("market.fields.warehouse"),
        exportValue: (row) => `${row.stationName} · ${row.warehouseCode}`,
        cell: (row) => (
          <span className="grid gap-0.5">
            <span>{row.stationName}</span>
            <span className="font-mono text-xs text-muted-foreground">{row.warehouseCode}</span>
          </span>
        ),
      },
      {
        id: "kind",
        header: t("market.counts.fields.kind"),
        exportValue: (row) => STOCK_COUNT_KIND_LABELS[row.kind],
        cell: (row) => STOCK_COUNT_KIND_LABELS[row.kind],
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => STOCK_COUNT_STATUS_LABELS[row.status],
        cell: (row) => (
          <span className="flex flex-wrap gap-1">
            <CountStatusBadge status={row.status} />
            {row.approvalPending ? (
              <StatusBadge tone="warning">{t("market.counts.approvalPending")}</StatusBadge>
            ) : null}
          </span>
        ),
      },
      {
        id: "progress",
        header: t("market.counts.fields.progress"),
        align: "right",
        exportValue: (row) => `${row.countedLineCount}/${row.lineCount}`,
        cell: (row) => (
          <span className="tabular">
            {t("market.counts.progressValue", { counted: row.countedLineCount, total: row.lineCount })}
          </span>
        ),
      },
      {
        id: "shortageValue",
        header: t("market.counts.fields.shortageValue"),
        align: "right",
        exportValue: (row) => moneyExport(row.shortageValue),
        cell: (row) => <MoneyText value={row.shortageValue} />,
      },
      {
        id: "surplusValue",
        header: t("market.counts.fields.surplusValue"),
        align: "right",
        exportValue: (row) => moneyExport(row.surplusValue),
        cell: (row) => <MoneyText value={row.surplusValue} />,
      },
      {
        id: "netValue",
        header: t("market.counts.fields.netValue"),
        align: "right",
        exportValue: (row) => moneyExport(row.netValue),
        cell: (row) => <MoneyText value={row.netValue} colorize className="font-medium" />,
      },
      {
        id: "ledger",
        header: t("market.fields.ledger"),
        defaultVisible: false,
        exportValue: (row) => row.ledgerStatus,
        cell: (row) => <LedgerStatus status={row.ledgerStatus} journalEntryId={row.journalEntryId} />,
      },
    ],
    [t],
  );

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stations.length === 0) return <NoStations />;

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <MarketStationField selection={station} />
        {permissions.canManageCounts ? (
          <Button className="ms-auto" onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            {t("market.counts.new")}
          </Button>
        ) : null}
      </Card>

      <DataTable
        caption={t("market.counts.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<ClipboardPen />}
            title={t("market.counts.emptyTitle")}
            description={t("market.counts.emptyDescription")}
          />
        }
        filters={
          <>
            <FormField label={t("common.status")} className="w-48">
              {(control) => (
                <Select
                  {...control}
                  value={search.status ?? ""}
                  onChange={(event) => setSearch({ status: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  {STOCK_COUNT_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {STOCK_COUNT_STATUS_LABELS[status]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("market.counts.fields.kind")} className="w-44">
              {(control) => (
                <Select
                  {...control}
                  value={search.kind ?? ""}
                  onChange={(event) => setSearch({ kind: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  {STOCK_COUNT_KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {STOCK_COUNT_KIND_LABELS[kind]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
          </>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          const rows = await fetchAllPages<StockCount>(
            async (cursor) =>
              (
                await api.GET("/market/counts", {
                  params: { query: countsQuery(companyId, filters, cursor, 200) as never },
                })
              ).data!,
          );
          await exportRowsToExcel({
            fileName: "stok-sayimlari",
            sheetName: t("menu.marketCounts"),
            columns: visible,
            rows,
          });
        }}
      />

      {permissions.canManageCounts ? (
        <CountDialog
          open={creating}
          onOpenChange={setCreating}
          companyId={companyId}
          stations={station.stations}
          defaultStationId={station.stationId}
          onCreated={(count) => void navigate({ to: "/market/sayimlar/$id", params: { id: count.id } })}
        />
      ) : null}
    </>
  );
}
